/**
 * `<CriticalsDeltaCard>` — a compact stat tile for the Tracking panel showing
 * the current open-critical count from the latest completed audit and the
 * signed delta vs the previous completed audit.
 *
 * Fits into the 4-up `StatTile` grid but keeps its own layout because the
 * delta line ("3 closed since last audit") is a sentence, not a percent chip.
 */
import { AlertOctagon, Loader2 } from 'lucide-react';
import { useAuditCriticalsDelta } from '@/features/actions/use-audit-criticals-delta';
import { cn } from '@/lib/cn';

export function CriticalsDeltaCard({
  projectId,
  className,
}: {
  projectId: string;
  className?: string;
}): JSX.Element {
  const q = useAuditCriticalsDelta(projectId);

  const body = (() => {
    if (q.isPending && !q.data) {
      return (
        <div className="mt-2 flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="h-3.5 w-3.5 animate-spin" />
          Loading…
        </div>
      );
    }
    if (q.isError || !q.data) {
      return (
        <div className="mt-2 text-sm text-muted-foreground">
          Couldn’t load audit deltas.
        </div>
      );
    }

    const { current, previous, delta, previous_audit_id } = q.data;

    // No completed audits yet.
    if (current == null) {
      return (
        <>
          <div className="mt-2 text-2xl font-semibold tabular leading-tight text-muted-foreground">
            —
          </div>
          <div className="mt-1.5 text-xs text-muted-foreground">
            No completed audits yet
          </div>
        </>
      );
    }

    // First audit — nothing to compare to.
    if (previous_audit_id == null || previous == null || delta == null) {
      return (
        <>
          <div className="mt-2 text-2xl font-semibold tabular leading-tight">
            {current}
          </div>
          <div className="mt-1.5 text-xs text-muted-foreground">
            First audit — nothing to compare
          </div>
        </>
      );
    }

    // Have a signed delta.
    let deltaLine: JSX.Element;
    if (delta === 0) {
      deltaLine = <span className="text-muted-foreground">no change since last audit</span>;
    } else if (delta < 0) {
      const n = Math.abs(delta);
      deltaLine = (
        <span className="text-success">
          {n} closed since last audit
        </span>
      );
    } else {
      deltaLine = (
        <span className="text-destructive">
          {delta} new since last audit
        </span>
      );
    }

    return (
      <>
        <div
          className={cn(
            'mt-2 text-2xl font-semibold tabular leading-tight',
            current > 0 ? 'text-destructive' : 'text-success',
          )}
        >
          {current}
        </div>
        <div className="mt-1.5 text-xs">{deltaLine}</div>
      </>
    );
  })();

  return (
    <div
      className={cn(
        'rounded-xl border border-border bg-card p-4 shadow-elevation-sm',
        className,
      )}
    >
      <div className="flex items-center justify-between gap-2">
        <span className="truncate text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
          Open criticals
        </span>
        <span
          className="flex h-7 w-7 items-center justify-center rounded-md bg-destructive/10 text-destructive"
          aria-hidden
        >
          <AlertOctagon className="h-3.5 w-3.5" />
        </span>
      </div>
      {body}
    </div>
  );
}
